import { auth, apiClient } from '../client.js';

/**
 * Authentication Service
 * Handles all authentication operations using Laravel Sanctum
 */
export class AuthService {
  constructor() {
    this.client = apiClient;
    this.currentUser = null;
  }

  /**
   * Login with email and password
   */
  async login(email, password) {
    try {
      const response = await auth.login(email, password);
      this.currentUser = response.user;
      return {
        user: response.user,
        token: response.token,
        success: true
      };
    } catch (error) {
      return {
        user: null,
        token: null,
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Register new user
   */
  async register(userData) {
    try {
      const response = await auth.register(userData);
      this.currentUser = response.user;
      return {
        user: response.user,
        token: response.token,
        success: true
      };
    } catch (error) {
      return {
        user: null,
        token: null,
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Logout current user
   */
  async logout() {
    try {
      await auth.logout();
      this.currentUser = null;
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get current authenticated user
   */
  async getCurrentUser() {
    try {
      if (!auth.isAuthenticated()) {
        return { user: null, success: false };
      }

      const response = await auth.getUser();
      this.currentUser = response.user || response.data;
      return {
        user: this.currentUser,
        success: true
      };
    } catch (error) {
      return {
        user: null,
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Alias for getCurrentUser - for compatibility
   */
  async me() {
    return await this.getCurrentUser();
  }

  /**
   * Refresh authentication token
   */
  async refreshToken() {
    try {
      const response = await auth.refreshToken();
      return {
        token: response.token,
        success: true
      };
    } catch (error) {
      return {
        token: null,
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Update user profile
   */
  async updateProfile(userData) {
    try {
      const response = await this.client.put('/api/auth/profile', userData);
      this.currentUser = response.user;
      return {
        user: response.user,
        success: true
      };
    } catch (error) {
      return {
        user: null,
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Change password
   */
  async changePassword(currentPassword, newPassword) {
    try {
      await this.client.put('/api/auth/password', {
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirmation: newPassword
      });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Request password reset
   */
  async requestPasswordReset(email) {
    try {
      await this.client.post('/api/auth/password/reset', { email });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Reset password with token
   */
  async resetPassword(token, email, password) {
    try {
      await this.client.post('/api/auth/password/reset/confirm', {
        token,
        email,
        password,
        password_confirmation: password
      });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return auth.isAuthenticated();
  }

  /**
   * Check if user has admin role
   */
  isAdmin() {
    return this.currentUser?.role === 'admin' || false;
  }

  /**
   * Get user permissions
   */
  getUserPermissions() {
    return this.currentUser?.permissions || [];
  }

  /**
   * Check if user has specific permission
   */
  hasPermission(permission) {
    const permissions = this.getUserPermissions();
    return permissions.includes(permission);
  }

  /**
   * Admin login with additional validation
   */
  async adminLogin(email, password) {
    const result = await this.login(email, password);
    
    if (result.success && !this.isAdmin()) {
      await this.logout();
      return {
        success: false,
        error: 'Keine Administrator-Berechtigung'
      };
    }
    
    return result;
  }

  /**
   * Link customer to authenticated user
   */
  async linkCustomer(customerId) {
    try {
      const response = await this.client.post('/api/auth/link-customer', {
        customer_id: customerId
      });
      return {
        success: true,
        customer: response.customer
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get linked customer data
   */
  async getLinkedCustomer() {
    try {
      const response = await this.client.get('/api/auth/customer');
      return {
        success: true,
        customer: response.customer
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }
}

// Export singleton instance for backward compatibility
const authService = new AuthService();
export default authService; 
